import { Col, Row } from "react-bootstrap";
import { brandsMasterDataApi, productBrandsMappingsApi } from "@/apis/apis";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useGet, usePut } from "@/apiHooks/HttpFunctions";

import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import { LIST_MODE } from "@/constant/Lists/listMode";
import ProductBrandInitialValues from "@/data/WebDataManagement/Mapping/ProductBrand/initial-values";
import ProductBrandSchema from "@/schema/WebDataManagement/Mapping/ProductBrand/ProductBrandSchema";
import RenderToast from "@/components/RenderToast";
import axiosInstance from "@/Axios/ConfiguredAxios";
import { debounce } from "@mui/material";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";

const ProductBrandModal = forwardRef((_, ref) => {
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [saveError, setSaveError] = useState(null);
  const resetError = useCallback(() => {
    setSaveError(null);
  }, []);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${productBrandsMappingsApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const listApi = `${brandsMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;

  useEffect(() => {
    if (!modalState.isOpen) return;

    const fetchDefaultOptions = async () => {
      setLoadingOptions(true);
      const response = await axiosInstance.get(listApi);

      if (response) {
        const options =
          response.data.data.map((val) => ({
            label: `${val.code} - ${val.name}`,
            value: val.id,
          })) ?? [];

        const selectedBrands = showData?.data?.brands || [];
        selectedBrands.forEach((brand) => {
          if (!options.some((opt) => opt.value === brand.id)) {
            options.unshift({
              label: `${brand.code} - ${brand.name}`,
              value: brand.id,
            });
          }
        });

        setDefaultOptions(options);
      }

      setLoadingOptions(false);
    };

    fetchDefaultOptions();
  }, [listApi, modalState, showData]);

  const debouncedFetchOptions = debounce(async (input, callback) => {
    setLoadingOptions(true);
    const response = await axiosInstance.get(`${listApi}${input}`);

    if (response) {
      const newOptions =
        response.data.data
          .filter((item) => item.id !== modalState.id)
          .map((item) => ({
            label: `${item.code} - ${item.name}`,
            value: item.id,
          })) ?? [];

      // Append new options to defaultOptions if not already present
      setDefaultOptions((prev) => {
        const existingIds = prev.map((opt) => opt.value);
        return [
          ...prev,
          ...newOptions.filter((option) => !existingIds.includes(option.value)),
        ];
      });

      callback(newOptions);
    }

    setLoadingOptions(false);
  }, 500);

  const getSelectedOption = () => {
    if (!formik.values.brand_ids?.length) return [];
    return formik.values.brand_ids.map((id) => {
      // Attempt to find the option in the defaultOptions
      const foundOption = defaultOptions.find((opt) => opt.value === id);
      if (foundOption) {
        return foundOption;
      }

      console.log();

      console.log(id);

      // Fallback: Dynamically create the option
      const matchingBrand = showData?.data?.brands.find((v) => v.id === id);
      return {
        label: matchingBrand
          ? `${matchingBrand.code} - ${matchingBrand.name}`
          : `Unknown (${id})`,
        value: id,
      };
    });
  };

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Product - Brand ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${productBrandsMappingsApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });

  useEffect(() => {
    setSaveError(saveEditError);
  }, [saveEditError]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ProductBrandInitialValues(modalState.id ? showData : null),
    validationSchema: ProductBrandSchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const payload = {
      brand_ids: values.brand_ids,
    };
    saveEdit(JSON.stringify(payload));
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  if (!showData) return null;

  return (
    <BasicModal
      header={modalState.id ? "Edit Product - Brand" : "Add Product - Brand"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            saveError={saveError}
            label="Product"
            disabled={true}
            value={`${showData.data.code} - ${showData.data.name}`}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            isMulti={true}
            label="Brands"
            placeholder="No brand selected. Type to search..."
            defaultOptions={defaultOptions}
            loadOptions={debouncedFetchOptions}
            isLoading={loadingOptions}
            value={getSelectedOption()}
            onChange={(selected) => {
              if (selected) {
                // Add selected option to defaultOptions if not already present
                setDefaultOptions((prev) => {
                  if (!prev.some((opt) => opt.value === selected.value)) {
                    return [
                      ...prev,
                      { label: selected.label, value: selected.value },
                    ];
                  }
                  return prev;
                });

                // Update formik value
                formik.setFieldValue(
                  "brand_ids",
                  selected.map((sel) => sel.value)
                );
              } else {
                formik.setFieldValue("brand_ids", []);
              }
            }}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default ProductBrandModal;
